import { createBrowserRouter } from "react-router-dom";
import Dashboard from "./components/dashboard/Dashboard";
import MeetingListPage from "./containers/MeetingListPage";
import {
    BrowserJoinMeetingPageController,
    CreateMeetingController,
    MeetingEditorController,
    MeetingPageController,
    MeetingViewController,
} from "./components/controllers";
import ManagerContentFrame from "./components/main-frame/ManagerContentFrame";
import Page from "./components/main-frame/Page";
import { HasPermission } from "./components/tools";
import { DebugControlPositionContext } from "./components/debug/tools";
import { DebugControlContainer } from "./components/debug/DebugControlContainer";
import Notification from "./containers/Notification";
import BackendErrorDialog from "./containers/BackendErrorDialog";
import { useDebugSettings } from "conference/tools";
import { useDebugControlPositionHandlerFactory } from "./components/debug/tools";
import * as React from "react";
import debug from "debug";
import { defineMessage } from "react-intl";

// Layout component that includes all providers
const AppLayout = ({ children }: { children: React.ReactNode }) => {
    const debugSettings = useDebugSettings();
    const debugControlPosition = useDebugControlPositionHandlerFactory({
        top: "calc(100% - 30px)",
        left: 5,
    });

    React.useEffect(() => {
        if (debugSettings.enabled) debug.enable(debugSettings.loggerNamespaces);
        else debug.disable();
    }, [debugSettings.enabled, debugSettings.loggerNamespaces]);

    return (
        <DebugControlPositionContext.Provider value={debugControlPosition}>
            <BackendErrorDialog />
            <DebugControlContainer />
            <Notification />
            {children}
        </DebugControlPositionContext.Provider>
    );
};

export const router = createBrowserRouter([
    {
        path: "/",
        element: (
            <AppLayout>
                <ManagerContentFrame>
                    <Page
                        title={defineMessage({ id: "title.home", defaultMessage: "Home" })}
                        subtitle={defineMessage({ id: "title.homeSubtitle", defaultMessage: "What's going on?" })}
                    >
                        <Dashboard />
                    </Page>
                </ManagerContentFrame>
            </AppLayout>
        ),
    },
    {
        path: "/meeting/list",
        element: (
            <AppLayout>
                <ManagerContentFrame>
                    <Page
                        title={defineMessage({ id: "title.futureList", defaultMessage: "Upcoming meetings" })}
                        subtitle={defineMessage({
                            id: "title.futureListSubtitle",
                            defaultMessage: "Your meetings what are not started yet",
                        })}
                    >
                        <MeetingListPage hideStarted />
                    </Page>
                </ManagerContentFrame>
            </AppLayout>
        ),
    },
    {
        path: "/past-meeting/list",
        element: (
            <AppLayout>
                <ManagerContentFrame>
                    <Page
                        title={defineMessage({ id: "title.pastList", defaultMessage: "Past meeting list" })}
                        subtitle={defineMessage({
                            id: "title.pastListSubtitle",
                            defaultMessage: "Your meetings what are finished",
                        })}
                    >
                        <MeetingListPage hideFuture />
                    </Page>
                </ManagerContentFrame>
            </AppLayout>
        ),
    },
    {
        path: "/meeting/create",
        element: (
            <AppLayout>
                <ManagerContentFrame>
                    <HasPermission name="create_meeting">
                        <CreateMeetingController />
                    </HasPermission>
                </ManagerContentFrame>
            </AppLayout>
        ),
    },
    {
        path: "/meeting/view/:id",
        element: (
            <AppLayout>
                <ManagerContentFrame>
                    <MeetingViewController />
                </ManagerContentFrame>
            </AppLayout>
        ),
    },
    {
        path: "/meeting/edit/:id",
        element: (
            <AppLayout>
                <ManagerContentFrame>
                    <MeetingEditorController />
                </ManagerContentFrame>
            </AppLayout>
        ),
    },
    {
        path: "/meeting/join/:id",
        element: (
            <AppLayout>
                <BrowserJoinMeetingPageController />
            </AppLayout>
        ),
    },
    {
        path: "/meeting/:id",
        element: (
            <AppLayout>
                <MeetingPageController />
            </AppLayout>
        ),
    },
]);
